import { motion } from 'framer-motion';
import { Target, Eye, Rocket, Users, Briefcase, ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../utils/animations';
import { Link } from 'react-router-dom';

const About = () => {
  const timeline = [
    { year: '2018', title: 'Launch Sequence Initiated', description: 'Founded by visionary entrepreneurs who saw the convergence of marketing and technology as the next frontier. Our mission began in a small office with big dreams and cutting-edge ideas.' },
    { year: '2019', title: 'First Orbital Success', description: 'Achieved breakthrough results for early clients, establishing our reputation for innovative digital solutions. Expanded team with top-tier talent from leading tech companies.' },
    { year: '2021', title: 'Constellation Expansion', description: 'Opened new service divisions and pioneered AI-driven marketing automation. Recognized as "Rising Star Agency" by Digital Marketing Institute.' },
    { year: '2023', title: 'Industry Leadership', description: 'Became thought leaders in marketing technology integration. Launched innovation lab and established strategic partnerships with major tech platforms.' },
    { year: '2025', title: 'Future Trajectory', description: 'Continuing to push boundaries with AI, AR/VR, and next-generation marketing technologies. Guiding brands toward their digital transformation destiny.' },
  ];

  const actions = [
    { icon: Rocket, title: 'Start Your Mission', description: 'Launch your digital transformation with our AI-powered solutions', link: '/contact' },
    { icon: Users, title: 'Join Our Team', description: 'Work with cutting-edge technology and brilliant minds', link: '/contact' },
    { icon: Briefcase, title: 'Partner With Us', description: 'Collaborate for mutual growth and innovation', link: '/contact' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-dark-surface"
    >
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-h1 font-bold mb-4">
            About <span className="gradient-text">STARZ Digital</span>
          </h1>
          <p className="text-body-lg text-text-secondary max-w-3xl mx-auto">
            Pioneering the future of AI-driven marketing automation since 2018
          </p>
        </motion.div>

        {/* Mission & Vision */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6 mb-12"
        >
          <motion.div variants={fadeInUp} className="glass-morphism p-6 rounded-2xl">
            <Target className="w-10 h-10 text-neon-purple mb-3" />
            <h2 className="text-h3 font-bold mb-3 gradient-text">Our Mission</h2>
            <p className="text-text-secondary">
              To revolutionize digital marketing through AI automation, helping businesses 5X their revenue while reducing manual effort. We're committed to building the most advanced, ethical marketing technology that delivers measurable results.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="glass-morphism p-6 rounded-2xl">
            <Eye className="w-10 h-10 text-neon-blue mb-3" />
            <h2 className="text-h3 font-bold mb-3 gradient-text">Our Vision</h2>
            <p className="text-text-secondary">
              To become the operating system for digital marketing worldwide, where every campaign is optimized in real-time by our AI. We envision a future where marketing is 100% data-driven, personalized, and delivers value to both businesses and consumers.
            </p>
          </motion.div>
        </motion.div>

        {/* Action Cards */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          {actions.map((action, index) => {
            const Icon = action.icon;
            return (
              <motion.div key={index} variants={fadeInUp}>
                <Link to={action.link} className="block glass-morphism p-5 rounded-2xl hover:shadow-glow-purple transition-all duration-300 group">
                  <Icon className="w-8 h-8 text-neon-purple mb-3" />
                  <h3 className="text-h4 font-bold mb-2">{action.title}</h3>
                  <p className="text-text-secondary text-sm mb-3">{action.description}</p>
                  <div className="flex items-center text-neon-purple text-sm font-medium">
                    Learn more
                    <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Origin Story Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-morphism p-8 rounded-3xl"
        >
          <h2 className="text-h2 font-bold mb-3 text-center gradient-text">Origin Story</h2>
          <p className="text-text-secondary text-center mb-8 max-w-3xl mx-auto">
            Every constellation has a beginning. Discover how STARZ Digital emerged from a vision to transform the marketing universe through innovative technology and stellar client experiences.
          </p>

          <div className="space-y-6">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-neon-purple to-neon-blue flex items-center justify-center">
                    <span className="text-white font-bold">{item.year}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-h4 font-bold mb-1">{item.title}</h3>
                  <p className="text-text-secondary text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default About;

